import React from 'react';
import { buttonClass } from './ui/Button';
import { apiFetchAuthed } from '../apiHelpers';
import useUserStore from '../stores/useUserStore';
import { recentRoutes } from './RecentRoutes';

/**
 * The one error boundary above the router. Without it, React 17 unmounts the
 * whole root on any render error and the person gets a white page with nothing
 * on it, not even the update prompt.
 *
 * The case this exists for: a tab still running an OLD build after a new
 * service worker took over (another tab clicked Update). Its in-memory code is
 * fine until it lazy-loads a chunk it has not visited yet (the barcode scanner
 * modal, the 3D flight path, the pilot simulator). The old chunk's URL is no
 * longer served, so `import()` rejects and Suspense rethrows into the tree.
 * That failure has one right answer, reload onto the new build, and this
 * boundary says so instead of going blank. Anything else lands on the generic
 * card, which at least keeps a Reload button on screen.
 *
 * Lives OUTSIDE `UpdatePrompt` on purpose: the prompt must survive the tree
 * under it dying.
 *
 * In a production build, a crash that is not a stale chunk files a bug report
 * by itself for a signed-in person (api/docs/features/flerpie-bug-reports.md,
 * "Crash reports"). Fire and forget: a failed filing shows nothing extra.
 */

interface State {
  error: Error | null;
  reportId: number | null;
}

const CHUNK_FAILURE = /ChunkLoadError|Loading chunk|Unexpected token '<'|Failed to fetch dynamically imported module|import\(\)/i;

export function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false;
  return CHUNK_FAILURE.test(error.name) || CHUNK_FAILURE.test(error.message);
}

export default class RootErrorBoundary extends React.Component<React.PropsWithChildren<{}>, State> {
  state: State = { error: null, reportId: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Render error caught at the root:', error);
    this.fileCrash(error, info);
  }

  private fileCrash(error: Error, info: React.ErrorInfo) {
    try {
      if (process.env.NODE_ENV !== 'production' || isChunkLoadError(error)) return;
      if (!useUserStore.getState().user?.loggedin) return;
      apiFetchAuthed('/handoffs/crash', {
        method: 'POST',
        silent: true,
        body: {
          name: error?.name || '',
          message: error?.message || String(error),
          stack: error?.stack || '',
          componentStack: info?.componentStack || '',
          path: window.location.pathname,
          recentPaths: recentRoutes(),
          version: process.env.REACT_APP_COMMIT_SHA || '',
          userAgent: navigator.userAgent,
        },
      })
        .then((res: { id?: unknown } | null) => {
          if (res && typeof res.id === 'number') this.setState({ reportId: res.id });
        })
        .catch(() => {});
    } catch {
      // Reporting must never be the second crash.
    }
  }

  render() {
    const { error, reportId } = this.state;
    if (!error) return this.props.children;
    const stale = isChunkLoadError(error);
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface p-6">
        <div role="alert" className="w-full max-w-md rounded-xl bg-surface-container-high p-6 space-y-3 shadow-xl">
          <h1 className="text-lg font-semibold text-on-surface">
            {stale ? 'This tab is on an older version' : 'Something went wrong on this page'}
          </h1>
          <p className="text-sm text-on-surface-variant">
            {stale
              ? 'A newer version of the app was installed while this tab was open, and the part it just asked for no longer exists. Reload to continue on the new version.'
              : 'The page hit an error it could not recover from. Reloading usually clears it. If it keeps happening, tell Mannie what you were doing.'}
          </p>
          {!stale && reportId !== null && (
            <p className="text-sm text-on-surface-variant tabular-nums animate-[showup_200ms_cubic-bezier(0.2,0,0,1)_both]">
              Reported automatically, report #{reportId}.
            </p>
          )}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className={buttonClass('filled', 'md')}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
